import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Truck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "arbe_promo_dismissed_at";
const RE_SHOW_HOURS = 24;

/**
 * Slim, polite sticky bottom banner promoting current offer.
 * - Hides for logged-in customers (already shopping)
 * - Hides for admin / driver / manager / cashier (staff don't need it)
 * - Dismissible with X, then re-appears after 24 h (so it's never annoying)
 * - Stays above the page footer, slides up on first view
 */
export default function PromoBar() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user) { setVisible(false); return; }
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const dismissedAt = Number(raw);
        if (!Number.isNaN(dismissedAt) && Date.now() - dismissedAt < RE_SHOW_HOURS * 3600 * 1000) {
          setVisible(false);
          return;
        }
      }
    } catch (_) { /* noop */ }
    // slight delay so it slides in after page paints
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setVisible(false);
  };

  if (!visible || user) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 transition-transform duration-500"
      style={{ transform: visible ? "translateY(0)" : "translateY(100%)" }}
      data-testid="promo-bar"
    >
      <div className="bg-gradient-to-r from-[#2F5233] via-[#1F3D24] to-[#2F5233] text-white shadow-[0_-10px_40px_rgba(0,0,0,0.15)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 py-2.5 sm:py-3 flex items-center gap-3">
          <div className="hidden sm:flex h-10 w-10 rounded-full bg-[#F3C97A]/20 items-center justify-center flex-shrink-0">
            <Truck className="h-5 w-5 text-[#F3C97A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3C97A] leading-tight">Fresh today</div>
            <div className="text-sm sm:text-base font-heading font-semibold leading-tight truncate">
              <span className="hidden sm:inline">Fresh meats &amp; groceries delivered to your door — </span>
              <span className="sm:hidden">Free delivery — </span>
              <span className="font-extrabold text-[#F3C97A]">FREE delivery</span> on orders over ₱1,000.
            </div>
          </div>
          <Link
            to="/register"
            className="bg-[#E67E22] hover:bg-[#D35400] text-white font-bold rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0 transition-colors"
            data-testid="promo-bar-cta"
          >
            Order now
          </Link>
          <button
            onClick={dismiss}
            className="p-1.5 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
            aria-label="Dismiss promotion"
            data-testid="promo-bar-close"
          >
            <X className="h-4 w-4 text-white/80" />
          </button>
        </div>
      </div>
    </div>
  );
}
